import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { CellImportDialog } from "../components/cell/CellImportDialog";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Search, Upload, Eye, EyeOff, Pencil, AlertTriangle, Home, Copy, UserCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { CellTextButton } from "../components/CellTextButton";
import { CellCallButton, canCellCall } from "../components/CellCallButton";
import { CellCallHistory } from "../components/CellCallHistory";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";
const inputCls = "w-full bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl focus:ring-2 focus:ring-[#0B5E4E]/20 focus:border-[#0B5E4E] outline-none transition-all px-3 py-2 text-sm";
const HOST_STATUSES = ["", "Interested", "Contacted", "Confirmed", "Unavailable"];

export default function CellResponses() {
  const { user } = useAuth();
  const canEdit = !!user?.caps?.cell_plan_edit;
  const canCall = canCellCall(user);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sp] = useSearchParams();
  const [q, setQ] = useState(sp.get("q") || "");
  const [filter, setFilter] = useState("all");
  const [area, setArea] = useState(sp.get("area") || "all");
  const [sector, setSector] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [reveal, setReveal] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [host, setHost] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cell/responses");
      setRows(data.responses || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = [...rows];
    const query = q.trim().toLowerCase();
    if (query) list = list.filter((r) => `${r.name} ${r.email} ${r.postcode} ${r.outward} ${r.phone} ${(r.phone || "").replace(/\D/g, "").replace(/^44/, "0")}`.toLowerCase().includes(query));
    if (area !== "all") list = list.filter((r) => r.outward === area);
    if (sector !== "all") list = list.filter((r) => r.sector === sector);
    if (filter === "needs_review") list = list.filter((r) => !r.postcode_valid);
    else if (filter === "duplicates") list = list.filter((r) => r.is_duplicate);
    else if (filter === "hosts") list = list.filter((r) => r.willing_to_host || r.host_status === "Confirmed");
    else if (filter === "no_availability") list = list.filter((r) => !r.availability);
    list.sort((a, b) => {
      if (sortBy === "postcode") return (a.postcode || "").localeCompare(b.postcode || "");
      if (sortBy === "area") return (a.outward || "").localeCompare(b.outward || "");
      return (a.name || "").localeCompare(b.name || "");
    });
    return list;
  }, [rows, q, filter, area, sector, sortBy]);

  const areaOptions = useMemo(() => Array.from(new Set(rows.map((r) => r.outward).filter(Boolean))).sort(), [rows]);
  const sectorOptions = useMemo(() => Array.from(new Set(rows.filter((r) => area === "all" || r.outward === area).map((r) => r.sector).filter(Boolean))).sort(), [rows, area]);

  const exportCsv = () => {
    const cols = ["name", "outward", "postcode", "host_status", "willing_to_host", "availability", "phone", "email"];
    const head = ["Name", "Area", "Postcode", "Host status", "Willing to host", "Availability", "Phone", "Email"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csvOut = [head.map(esc).join(","), ...filtered.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
    const blob = new Blob([csvOut], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "cell-responses.csv"; a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} responses`);
  };

  const counts = useMemo(() => ({
    total: rows.length,
    needsReview: rows.filter((r) => !r.postcode_valid).length,
    duplicates: rows.filter((r) => r.is_duplicate).length,
    hosts: rows.filter((r) => r.willing_to_host || r.host_status === "Confirmed").length,
  }), [rows]);

  const mask = (v) => (reveal || !v ? v || "—" : "•••••");


  const saveEdit = async () => {
    try {
      await api.put(`/cell/responses/${edit.record_id}`, {
        name: edit.name, phone: edit.phone, email: edit.email, address: edit.address, postcode: edit.postcode, notes: edit.notes,
      });
      toast.success("Response updated"); setEdit(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveHost = async () => {
    try {
      await api.post("/cell/hosts", {
        record_id: host.record_id, name: host.name, postcode: host.postcode,
        status: host.status || "Interested", capacity: Number(host.capacity) || 15,
        notes: host.notes || "", availability: host.availability || "", household_id: host.household_id || "",
      });
      toast.success("Host & planning saved"); setHost(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="cell-responses-page">
      <div className="sj-page-hero flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-head text-3xl font-semibold" style={{ color: "var(--sj-text)" }}>Cell Group — Responses</h1>
          <p className="text-sm mt-1" style={{ color: "var(--sj-text-2)" }}>The signup register, organised by postcode. Contact details are hidden by default.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="cell-reveal-toggle" onClick={() => setReveal((v) => !v)} className="inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
            {reveal ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />} {reveal ? "Hide contacts" : "Reveal contacts"}
          </button>
          <button data-testid="cell-export-csv" onClick={exportCsv} className="inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
            <Upload className="h-4 w-4 rotate-180" /> Export CSV
          </button>
          {canEdit && (
            <button data-testid="cell-import-button" onClick={() => setImportOpen(true)} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium text-white" style={{ background: "var(--sj-primary)" }}>
              <Upload className="h-4 w-4" /> Import responses
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[["Total responses", counts.total, "all"], ["Need postcode review", counts.needsReview, "needs_review"], ["Possible duplicates", counts.duplicates, "duplicates"], ["Possible hosts", counts.hosts, "hosts"]].map(([label, n, key]) => (
          <button key={key} data-testid={`cell-stat-${key}`} onClick={() => setFilter(key)} className={`${CARD} p-4 text-left transition-all ${filter === key ? "ring-2 ring-[#0B5E4E]" : ""}`}>
            <div className="text-2xl font-semibold" style={{ color: "var(--sj-primary)" }}>{n}</div>
            <div className="text-xs mt-0.5" style={{ color: "var(--sj-text-2)" }}>{label}</div>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: "var(--sj-primary)" }} />
          <input data-testid="cell-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, postcode…" className="w-full bg-white border-2 border-[#0B5E4E] rounded-xl outline-none focus:ring-2 focus:ring-[#0B5E4E]/30 pl-10 pr-4 py-2.5 text-sm" />
        </div>
        <select data-testid="cell-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className={inputCls + " max-w-[200px]"}>
          <option value="all">All responses</option>
          <option value="needs_review">Need postcode review</option>
          <option value="duplicates">Possible duplicates</option>
          <option value="hosts">Possible hosts</option>
          <option value="no_availability">No availability recorded</option>
        </select>
        <select data-testid="cell-area" value={area} onChange={(e) => { setArea(e.target.value); setSector("all"); }} className={inputCls + " max-w-[160px]"}>
          <option value="all">All areas</option>
          {areaOptions.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select data-testid="cell-sector" value={sector} onChange={(e) => setSector(e.target.value)} className={inputCls + " max-w-[160px]"}>
          <option value="all">All sectors</option>
          {sectorOptions.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="cell-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={inputCls + " max-w-[180px]"}>
          <option value="name">Sort by name</option>
          <option value="postcode">Sort by postcode</option>
          <option value="area">Sort by postcode area</option>
        </select>
      </div>

      <div className={`${CARD} overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider" style={{ color: "var(--sj-text-3)", background: "#F7F7F5" }}>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Postcode</th>
                <th className="px-4 py-3">Availability</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Host</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center" style={{ color: "var(--sj-text-3)" }}>Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center" style={{ color: "var(--sj-text-3)" }}>No responses. {canEdit ? "Import your Google Form responses to begin." : ""}</td></tr>
              ) : filtered.map((r) => (
                <tr key={r.record_id} data-testid={`cell-row-${r.record_id}`} className="border-t" style={{ borderColor: "var(--sj-border)" }}>
                  <td className="px-4 py-3">
                    <div className="font-medium flex items-center gap-1.5" style={{ color: "var(--sj-text)" }}>
                      {r.name || "—"}
                      {r.is_duplicate && <span title="Possible duplicate" className="inline-flex"><Copy className="h-3.5 w-3.5" style={{ color: "#D97706" }} /></span>}
                      {r.household_id && <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: "#E3F1EC", color: "#0B5E4E" }}>{r.household_id}</span>}
                    </div>
                    <div className="text-xs" style={{ color: "var(--sj-text-3)" }}>{r.source === "portal" ? "Portal signup" : r.source === "form" ? "Response form" : "Imported"}
                      {r.member_ref && <span data-testid={`cell-linked-member-${r.record_id}`} title={`Linked to member record${r.member_name ? `: ${r.member_name}` : ""}`} className="ml-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: "#E3F1EC", color: "var(--sj-primary)" }}><UserCheck className="h-3 w-3" /> Member</span>}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1">
                      {r.postcode || "—"}
                      {!r.postcode_valid && <span title="Postcode needs review" className="inline-flex"><AlertTriangle className="h-3.5 w-3.5" style={{ color: "#D97706" }} /></span>}
                    </span>
                  </td>
                  <td className="px-4 py-3" style={{ color: r.availability ? "var(--sj-text-2)" : "#D97706" }}>{r.availability || "Not set"}</td>
                  <td className="px-4 py-3 text-xs" style={{ color: "var(--sj-text-2)" }}>
                    <div className="flex items-center gap-1">{mask(r.phone)}{r.phone && canCall && <><CellCallButton recordId={r.record_id} name={r.name} label="" /><CellTextButton recordId={r.record_id} name={r.name} label="" /></>}</div>
                    <div>{mask(r.email)}</div>
                  </td>
                  <td className="px-4 py-3">
                    {r.host_status ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: r.host_status === "Confirmed" ? "#E3F1EC" : "#FBEEE6", color: r.host_status === "Confirmed" ? "#0B5E4E" : "#B45309" }}>{r.host_status}{r.host_capacity ? ` · ${r.host_capacity}` : ""}</span> : (r.willing_to_host ? <span className="text-xs" style={{ color: "var(--sj-text-3)" }}>Willing</span> : "—")}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {canEdit && <button data-testid={`cell-host-${r.record_id}`} onClick={() => setHost({ record_id: r.record_id, name: r.name, postcode: r.postcode, status: r.host_status || "Interested", capacity: r.host_capacity || 15, notes: "", availability: r.availability || "", household_id: r.household_id || "" })} title="Host / planning" className="p-2 rounded-lg hover:bg-[#E3F1EC]" style={{ color: "var(--sj-primary)" }}><Home className="h-4 w-4" /></button>}
                      {canCall && <CellCallHistory recordId={r.record_id} name={r.name} />}
                      {canEdit && <button data-testid={`cell-edit-${r.record_id}`} onClick={() => setEdit({ ...r })} title="Edit response" className="p-2 rounded-lg hover:bg-[#E3F1EC]" style={{ color: "var(--sj-secondary)" }}><Pencil className="h-4 w-4" /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <CellImportDialog open={importOpen} onOpenChange={setImportOpen} onDone={load} />

      {/* Edit response dialog */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="cell-edit-dialog">
          <DialogHeader><DialogTitle>Edit response</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              {[["name", "Name"], ["phone", "Phone"], ["email", "Email"], ["address", "Address"], ["postcode", "Postcode"], ["notes", "Notes"]].map(([k, label]) => (
                <div key={k}>
                  <label className="text-xs font-medium" style={{ color: "var(--sj-text-2)" }}>{label}</label>
                  <input data-testid={`cell-edit-${k}`} value={edit[k] || ""} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} className={inputCls} />
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEdit(null)} className="rounded-full px-4 py-2 text-sm border" style={{ borderColor: "var(--sj-border)" }}>Cancel</button>
            <button data-testid="cell-edit-save" onClick={saveEdit} className="rounded-full px-5 py-2 text-sm text-white font-medium" style={{ background: "var(--sj-primary)" }}>Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Host / planning dialog */}
      <Dialog open={!!host} onOpenChange={(o) => !o && setHost(null)}>
        <DialogContent data-testid="cell-host-dialog">
          <DialogHeader><DialogTitle>Host & planning details</DialogTitle></DialogHeader>
          {host && (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium" style={{ color: "var(--sj-text-2)" }}>Host status</label>
                <select data-testid="cell-host-status" value={host.status} onChange={(e) => setHost({ ...host, status: e.target.value })} className={inputCls}>
                  {HOST_STATUSES.filter(Boolean).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium" style={{ color: "var(--sj-text-2)" }}>Capacity (1–15, includes host & leader)</label>
                <input data-testid="cell-host-capacity" type="number" min={1} max={15} value={host.capacity} onChange={(e) => setHost({ ...host, capacity: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className="text-xs font-medium" style={{ color: "var(--sj-text-2)" }}>Meeting availability</label>
                <input data-testid="cell-host-availability" value={host.availability} onChange={(e) => setHost({ ...host, availability: e.target.value })} placeholder="e.g. Tuesday 7pm" className={inputCls} />
              </div>
              <div>
                <label className="text-xs font-medium" style={{ color: "var(--sj-text-2)" }}>Household ID (optional — keeps a household together)</label>
                <input data-testid="cell-host-household" value={host.household_id} onChange={(e) => setHost({ ...host, household_id: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className="text-xs font-medium" style={{ color: "var(--sj-text-2)" }}>Notes</label>
                <input data-testid="cell-host-notes" value={host.notes} onChange={(e) => setHost({ ...host, notes: e.target.value })} className={inputCls} />
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setHost(null)} className="rounded-full px-4 py-2 text-sm border" style={{ borderColor: "var(--sj-border)" }}>Cancel</button>
            <button data-testid="cell-host-save" onClick={saveHost} className="rounded-full px-5 py-2 text-sm text-white font-medium" style={{ background: "var(--sj-primary)" }}>Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
